import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Layout from "../../components/Layout";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Dialog from "../../components/ui/Dialog";
import Input from "../../components/ui/Input";
import DataTable, {
  Column,
  DataTableFetchParams,
  DataTableFilterDef,
} from "../../components/ui/DataTable";
import { usersService } from "../../services/users.service";
import { marketplaceService } from "../../services/marketplace.service";
import { UserDocumentFiles } from "../../dtos/user.dto";
import { User } from "../../interfaces/user.interface";
import { Permission } from "../../permissions/permission.enum";
import { useAuth } from "../../context/AuthContext";
import { UserRole } from "../../enums/user-role.enum";
import { createUserSchema, updateUserSchema } from "./validations/user.schema";
import { getErrorField, getErrorMsg } from "../../utils/error";
import { useConfirm } from '../../hooks/useConfirm';
import { ACCOUNT_TYPE_DOCUMENTS, AVATAR_DOCUMENT, USER_CURRENCIES, VERIFICATION_STATUS_BADGE } from "./users.constants";
import { UserFormData } from "./users.types";
import { downloadCsv } from "../../utils/csv";

function triggerBlobDownload(blob: Blob, sourceUrl: string) {
  const filename = sourceUrl.split("/").pop()?.split("?")[0] ?? "document";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function BrokerDocRow({ label, url, onDownload }: { label: string; url: string; onDownload: () => Promise<void> }) {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const filename = url.split("/").pop()?.split("?")[0] ?? "document";

  const handleClick = async () => {
    setError(null);
    setDownloading(true);
    try {
      await onDownload();
    } catch {
      setError("Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-gray-500 dark:text-gray-400 w-32 shrink-0">{label}</span>
      <span className="text-sm font-mono text-gray-700 dark:text-gray-300 truncate flex-1">{filename}</span>
      <Button type="button" size="sm" variant="secondary" onClick={handleClick} disabled={downloading}>
        {downloading ? "Downloading…" : "Download"}
      </Button>
      {error && <span className="text-xs text-red-500">{error}</span>}
    </div>
  );
}

const UsersList = () => {
  const { t } = useTranslation("common");
  const { confirm: confirmDialog, ConfirmDialog } = useConfirm();
  const { hasPermission, user: currentUser } = useAuth();

  const [activeTab, setActiveTab] = useState<"users" | "deletion-requests">("users");
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [companyAction, setCompanyAction] = useState<"transfer" | "delete">("transfer");
  const [newOwnerId, setNewOwnerId] = useState<string>("");
  const [transferCandidates, setTransferCandidates] = useState<{ id: string; fullName: string; email: string }[]>([]);
  const [deletionReason, setDeletionReason] = useState("");
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [editing, setEditing] = useState<User | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [locations, setLocations] = useState<string[]>([]);
  const [files, setFiles] = useState<UserDocumentFiles>({});
  // Company paperwork lives on the company row, not the user row, so editing a
  // company account needs the company to know which documents are still missing.
  const [editingCompany, setEditingCompany] = useState<{ logo?: string; brFileUrl?: string } | null>(null);
  const [showFilePasswordInput, setShowFilePasswordInput] = useState(false);
  const [filePasswordInput, setFilePasswordInput] = useState("");
  const [fileDownloadError, setFileDownloadError] = useState<string | null>(null);
  const [downloadingDoc, setDownloadingDoc] = useState(false);
  const [showMasterKeyInput, setShowMasterKeyInput] = useState(false);
  const [masterKeyInput, setMasterKeyInput] = useState("");
  const [revealError, setRevealError] = useState<string | null>(null);
  const [revealingPassword, setRevealingPassword] = useState(false);
  const [docPassword, setDocPassword] = useState<string | null>(null);

  const [deletionRequests, setDeletionRequests] = useState<User[]>([]);
  const [deletionRequestsLoading, setDeletionRequestsLoading] = useState(false);
  const [deletionRequestsError, setDeletionRequestsError] = useState<string | null>(null);

  const canDelete = hasPermission(Permission.USERS_DELETE);
  const canUpdate = hasPermission(Permission.USERS_UPDATE);
  const canCreate = hasPermission(Permission.USERS_CREATE);
  const navigate = useNavigate();

  const [exportingReferrals, setExportingReferrals] = useState(false);
  const [referralExportError, setReferralExportError] = useState<string | null>(null);

  const handleExportReferrals = async () => {
    setExportingReferrals(true);
    setReferralExportError(null);
    try {
      const rows = await usersService.listReferrals();
      if (rows.length === 0) {
        setReferralExportError(t("no_referral_users"));
        return;
      }
      downloadCsv(
        "referral-users.csv",
        rows.map((r) => ({
          [t("user_name")]: r.userName,
          [t("user_email")]: r.userEmail,
          [t("referral_user_name")]: r.referrerName,
          [t("referral_email")]: r.referrerEmail,
          [t("referral_username")]: r.referrerUsername ?? "",
          [t("referral_matched_by")]: t(`referral_matched_by_${r.matchedBy.toLowerCase()}`),
          [t("referral_user_id")]: r.referredById,
          [t("created_at")]: new Date(r.createdAt).toLocaleDateString(),
        })),
      );
    } catch (err) {
      setReferralExportError(getErrorMsg(err));
    } finally {
      setExportingReferrals(false);
    }
  };

  const [exportingFallback, setExportingFallback] = useState(false);
  const [fallbackExportError, setFallbackExportError] = useState<string | null>(null);

  const handleExportOtpFallback = async () => {
    setExportingFallback(true);
    setFallbackExportError(null);
    try {
      // No dedicated endpoint: the users list already filters on the flag, and
      // take maxes out at 1000 server-side. Add a streaming export if the count
      // ever gets near that.
      const { data } = await usersService.list({
        usedOtpFallback: true,
        take: 1000,
        orderBy: "createdAt",
        order: "DESC",
      });
      if (data.length === 0) {
        setFallbackExportError(t("no_otp_fallback_users"));
        return;
      }
      downloadCsv(
        "otp-fallback-users.csv",
        data.map((u) => ({
          [t("user_name")]: u.fullName ?? "",
          [t("username")]: u.username ?? "",
          [t("user_email")]: u.email,
          [t("phone")]: u.phone ?? "",
          [t("account_type")]: u.accountType ?? "",
          [t("registration_status")]: u.verificationStatus ? t(`verification_${u.verificationStatus}`) : "",
          [t("otp_fallback_code")]: u.usedOtpFallbackCode ?? "",
          [t("created_at")]: u.createdAt ? new Date(u.createdAt).toLocaleDateString() : "",
        })),
      );
    } catch (err) {
      setFallbackExportError(getErrorMsg(err));
    } finally {
      setExportingFallback(false);
    }
  };

  const loadDeletionRequests = useCallback(async () => {
    setDeletionRequestsLoading(true);
    setDeletionRequestsError(null);
    try {
      const data = await usersService.listDeletionRequests();
      setDeletionRequests(data);
    } catch (err) {
      setDeletionRequestsError(getErrorMsg(err));
    } finally {
      setDeletionRequestsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "deletion-requests") {
      loadDeletionRequests();
    }
  }, [activeTab, loadDeletionRequests]);

  // ponytail: fetched on dialog open rather than on mount — admins who never open
  // the form don't pay for the call, and the list is fresh after a company is added.
  useEffect(() => {
    if (!dialogOpen) return;
    marketplaceService
      // status is not optional in effect — the endpoint defaults a missing param to
      // "pending", which would hide every approved company from the broker selector.
      .getCompanies({ status: "active", page: 1, take: 200 })
      .then((res) => setCompanies(res.data.map((c) => ({ id: c.id, name: c.name }))))
      .catch(() => setCompanies([]));

    // Suggestions only — the field stays free text, matching what the app accepts.
    // A failed fetch degrades to a plain input rather than blocking the form.
    usersService
      .listLocations()
      .then((rows) => setLocations(rows.map((l) => l.displayName)))
      .catch(() => setLocations([]));
  }, [dialogOpen]);

  const handleConfirmDeletion = async (id: string) => {
    if (!await confirmDialog(t('confirm_delete_user_pii'))) return;
    try {
      await usersService.confirmDeletion(id);
      loadDeletionRequests();
    } catch (err) {
      alert(getErrorMsg(err));
    }
  };

  const handleCancelDeletion = async (id: string) => {
    try {
      await usersService.cancelDeletion(id);
      loadDeletionRequests();
    } catch (err) {
      alert(getErrorMsg(err));
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    // Aliased — `setError` is already the page-level list error setter.
    setError: setFieldError,
    formState: { errors },
  } = useForm<UserFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(editing ? updateUserSchema : createUserSchema) as any,
  });

  const selectedAccountType = watch("accountType") ?? "personal";
  const accountTypeDocuments =
    ACCOUNT_TYPE_DOCUMENTS[selectedAccountType] ?? ACCOUNT_TYPE_DOCUMENTS.personal;
  const visibleDocumentFields = [...accountTypeDocuments, AVATAR_DOCUMENT];

  // Edit mode offers only the documents this account never uploaded — the backend
  // rejects a field that already holds a file rather than replacing it, because the
  // stored copy is encrypted under a password that has already been mailed out.
  const existingDocumentUrls: Partial<Record<keyof UserDocumentFiles, string | undefined>> = {
    idFile: editing?.idFileUrl,
    licenseFile: editing?.licenseFileUrl,
    credentialsFile: editing?.credentialsFileUrl,
    companyLogo: editingCompany?.logo,
    brFile: editingCompany?.brFileUrl,
  };
  // Until the company row loads there is no way to tell which of its documents are
  // missing, so offer none rather than guessing and letting the backend 409.
  const missingDocumentFields =
    selectedAccountType === "company" && !editingCompany
      ? []
      : accountTypeDocuments.filter(([field]) => !existingDocumentUrls[field]);

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const isActiveValue = params.filters?.isActive;
    const parsedIsActive =
      isActiveValue === "true"
        ? true
        : isActiveValue === "false"
          ? false
          : undefined;

    const response = await usersService.list({
      page: params.page,
      take: params.pageSize,
      orderBy: params.sortBy || "createdAt",
      order: params.sortOrder || "DESC",
      search: params.search || undefined,
      role: params.filters?.role || undefined,
      isActive: parsedIsActive,
      accountStatus: params.filters?.accountStatus || undefined,
      // Only "true" is meaningful — there is no reason to list users who did NOT
      // use the fallback, that is the unfiltered table.
      usedOtpFallback:
        params.filters?.usedOtpFallback === "true" ? true : undefined,
    });

    return {
      data: response.data,
      total: response.meta.itemCount,
      totalPages: response.meta.pageCount,
    };
  }, []);

  const openCreate = () => {
    setEditing(null);
    reset({
      email: "",
      fullName: "",
      username: "",
      phone: "",
      location: "",
      whatsappNo: "",
      role: "",
      isActive: true,
      preferredCurrency: "USD",
      accountType: "personal",
      markVerified: false,
      companyId: "",
      companyName: "",
      companyTradingName: "",
      companyBio: "",
    });
    setFiles({});
    setEditingCompany(null);
    setSubmitError(null);
    setDialogOpen(true);
  };

  const openEdit = (user: User) => {
    setEditing(user);
    reset({
      email: user.email,
      fullName: user.fullName ?? "",
      username: user.username ?? "",
      phone: user.phone ?? "",
      location: user.location ?? "",
      whatsappNo: user.whatsappNo ?? "",
      role: String(user.role) as UserRole,
      isActive: !!user.isActive,
      preferredCurrency: user.preferredCurrency ?? "USD",
      accountType: (user.accountType as UserFormData["accountType"]) ?? "personal",
      companyId: user.companyId ?? "",
    });
    setFiles({});
    setEditingCompany(null);
    // GET /admin/companies/:id is gated on users.read, the same permission as this
    // page, so no admin who can open this dialog will be refused. A failure just
    // leaves the company document inputs hidden.
    if (user.accountType === "company" && user.companyId) {
      marketplaceService
        .getCompany(user.companyId)
        .then((c) => setEditingCompany({ logo: c.logo, brFileUrl: c.brFileUrl }))
        .catch(() => setEditingCompany(null));
    }
    setSubmitError(null);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditing(null);
    setFiles({});
    setEditingCompany(null);
    setShowFilePasswordInput(false);
    setFilePasswordInput("");
    setFileDownloadError(null);
    setDownloadingDoc(false);
    setShowMasterKeyInput(false);
    setMasterKeyInput("");
    setRevealError(null);
    setRevealingPassword(false);
    setDocPassword(null);
  };

  const handleDownloadDoc = async () => {
    if (!editing) return;
    setFileDownloadError(null);
    setDownloadingDoc(true);
    try {
      const blob = await usersService.downloadIdDocument(editing.id, filePasswordInput);
      const ext = editing.idFileUrl?.split(".").pop() || "bin";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `id-document-${editing.id}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setFileDownloadError(t("file_password_decrypt_error"));
    } finally {
      setDownloadingDoc(false);
    }
  };

  const handleRevealPassword = async () => {
    if (!editing) return;
    setRevealError(null);
    setRevealingPassword(true);
    try {
      const result = await usersService.getDocumentPassword(editing.id, masterKeyInput);
      setDocPassword(result?.password ?? null);
    } catch {
      setRevealError(t("decrypt_error"));
    } finally {
      setRevealingPassword(false);
    }
  };

  const onSubmit = async (data: UserFormData) => {
    setSubmitError(null);
    if (!(await confirmDialog(t(editing ? "confirmUpdateUser" : "confirmCreateUser")))) return;

    try {
      if (editing) {
        await usersService.update(editing.id, {
          email: data.email,
          fullName: data.fullName,
          username: data.username || undefined,
          phone: data.phone || undefined,
          // null, not undefined — an omitted key leaves the old value in place.
          location: data.location || null,
          whatsappNo: data.whatsappNo || null,
          isActive: data.isActive,
          preferredCurrency: data.preferredCurrency || undefined,
          ...(editing.accountType === "broker" || editing.accountType === "company"
            ? { charterBookingMode: editing.charterBookingMode ?? "NON_INSTANT_ONLY" }
            : {}),
          // Only sent when the broker has no company yet — the API rejects reassignment.
          ...(data.companyId && !editing.companyId ? { companyId: data.companyId } : {}),
        });
        // Separate call: the PATCH route is JSON-only. Runs after the update so a
        // rejected field change doesn't leave documents uploaded against stale data.
        const newDocuments = Object.fromEntries(
          missingDocumentFields
            .filter(([field]) => files[field])
            .map(([field]) => [field, files[field]]),
        );
        if (Object.keys(newDocuments).length > 0) {
          await usersService.uploadDocuments(editing.id, newDocuments);
        }
      } else {
        await usersService.createWithDocuments(
          {
            email: data.email,
            fullName: data.fullName,
            username: data.username || undefined,
            phone: data.phone || undefined,
            location: data.location || undefined,
            whatsappNo: data.whatsappNo || undefined,
            // role is deliberately omitted — the users.role column defaults to
            // "BUYER", which is what the mobile register flow writes.
            isActive: data.isActive,
            preferredCurrency: data.preferredCurrency || undefined,
            accountType: data.accountType,
            companyId: data.accountType === "broker" ? data.companyId || undefined : undefined,
            companyName: data.accountType === "company" ? data.companyName || undefined : undefined,
            companyTradingName:
              data.accountType === "company" ? data.companyTradingName || undefined : undefined,
            companyBio: data.accountType === "company" ? data.companyBio || undefined : undefined,
            markVerified: data.markVerified,
          },
          // Switching account type unmounts the inputs but leaves their picks in
          // `files`, so send only what the chosen type actually asks for.
          Object.fromEntries(
            visibleDocumentFields
              .filter(([field]) => files[field])
              .map(([field]) => [field, files[field]]),
          ),
        );
      }
      closeDialog();
      setTableRefreshKey((prev) => prev + 1);
    } catch (err) {
      const message = getErrorMsg(err);
      // Duplicate email/phone conflicts name the offending field — pin the message
      // to that input instead of only showing it in the banner.
      const field = getErrorField(err);
      if (field === "email" || field === "phone") {
        setFieldError(field, { type: "server", message });
      }
      setSubmitError(message);
    }
  };

  const confirmDelete = async (u: User) => {
    setUserToDelete(u);
    setCompanyAction("transfer");
    setNewOwnerId("");
    setTransferCandidates([]);
    setDeletionReason("");
    setDeleteError(null);
    setDeleteDialogOpen(true);
    if (u.accountType === "company") {
      try {
        const candidates = await usersService.getTransferCandidates(u.id);
        setTransferCandidates(candidates);
        if (candidates.length === 0) setCompanyAction("delete");
      } catch (err) {
        setDeleteError(getErrorMsg(err));
      }
    }
  };

  const executeDelete = async () => {
    if (!userToDelete) return;
    const isOwner = userToDelete.accountType === "company";
    if (isOwner && companyAction === "transfer" && !newOwnerId) {
      setDeleteError(t("new_owner_required"));
      return;
    }
    try {
      await usersService.requestDeletion(userToDelete.id, {
        companyAction: isOwner ? companyAction : undefined,
        newOwnerId: isOwner && companyAction === "transfer" ? newOwnerId : undefined,
        reason: deletionReason || undefined,
      });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setTableRefreshKey((prev) => prev + 1);
    } catch (err) {
      setDeleteError(getErrorMsg(err));
    }
  };

  const columns: Column<User>[] = [
    {
      key: "fullName",
      header: "User",
      sortable: true,
      render: (u) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {u.fullName}
          </div>
          {u.username && (
            <div className="text-xs text-gray-500 dark:text-gray-400">
              @{u.username}
            </div>
          )}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {u.email}
          </div>
          {u.phone && (
            <div className="text-xs text-gray-400 dark:text-gray-500">
              {u.phone}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "accountType",
      header: "Account Type",
      render: (u) => {
        const accountType = u.accountType || "personal";
        const styles: Record<string, string> = {
          personal: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
          company: "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
          broker: "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200",
        };
        return (
          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${styles[accountType] ?? styles.personal}`}>
            {accountType}
          </span>
        );
      },
    },
    {
      key: "charterBookingMode",
      header: t("charterBookingMode"),
      render: (u) =>
        u.accountType === "broker" || u.accountType === "company" ? (
          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900 dark:text-sky-200">
            {t(`charterBookingMode_${u.charterBookingMode ?? "NON_INSTANT_ONLY"}`)}
          </span>
        ) : null,
    },
    {
      key: "isActive",
      header: t("status"),
      sortable: true,
      render: (u) => (
        <div className="flex flex-col gap-1">
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
              u.isActive
                ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
            }`}
          >
            {u.isActive ? t("active") : t("inactive")}
          </span>
          {u.isVerified && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-200">
              {t("phone_verified")}
            </span>
          )}
          {u.accountStatus === "pending_deletion" && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
              {t("pending_deletion")}
            </span>
          )}
          {u.accountStatus === "anonymized" && (
            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
              {t("anonymized")}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "registrationStatus",
      header: t("registration_status"),
      render: (u) =>
        u.verificationStatus ? (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${VERIFICATION_STATUS_BADGE[u.verificationStatus]}`}
          >
            {t(`verification_${u.verificationStatus}`)}
          </span>
        ) : null,
    },
    {
      key: "usedOtpFallback",
      header: t("otp_fallback"),
      render: (u) =>
        u.usedOtpFallback ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200 font-mono">
            {/* Older rows recorded only the flag, so fall back to the label. */}
            {u.usedOtpFallbackCode ?? t("otp_fallback_used")}
          </span>
        ) : (
          <span className="text-xs text-gray-400">{t("otp_fallback_not_used")}</span>
        ),
    },
    {
      key: "pointsBalance",
      header: "Points",
      render: (u) => (
        <span className="text-sm text-gray-700 dark:text-gray-300">
          {u.pointsBalance ?? 0}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      className: "text-right",
      render: (u) => (
        <div className="space-x-2 font-medium">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/users/${u.id}/points`)}
          >
            {t("point_history")}
          </Button>
          {canUpdate && (
            <Button size="sm" variant="secondary" onClick={() => openEdit(u)}>
              {t("edit")}
            </Button>
          )}
          {canDelete && currentUser?.id !== u.id && (
            <Button
              size="sm"
              variant="danger"
              onClick={() => confirmDelete(u)}
            >
              {t("schedule_deletion")}
            </Button>
          )}
        </div>
      ),
    },
  ];

  const filterDefs: DataTableFilterDef[] = [
    {
      key: "role",
      label: "Role",
      type: "select",
      options: Object.values(UserRole).map((r) => ({ label: r, value: r })),
    },
    {
      key: "isActive",
      label: t("status"),
      type: "select",
      options: [
        { label: t("active"), value: "true" },
        { label: t("inactive"), value: "false" },
      ],
    },
    {
      key: "usedOtpFallback",
      label: t("otp_fallback"),
      type: "select",
      options: [{ label: t("otp_fallback_used"), value: "true" }],
    },
    {
      key: "accountStatus",
      label: t("account_status"),
      type: "select",
      options: [
        { label: t("active"), value: "active" },
        { label: t("pending_deletion"), value: "pending_deletion" },
        { label: t("anonymized"), value: "anonymized" },
      ],
    },
  ];

  return (
    <Layout title={t("users")}>
      <div className="space-y-6 h-full flex flex-col">
        <Card className="p-4 shadow-sm flex-shrink-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-semibold tracking-tight">
                {t("users")}
              </h1>
              <div className="flex border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setActiveTab("users")}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === "users"
                      ? "border-blue-500 text-blue-600 dark:text-blue-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  }`}
                >
                  All Users
                </button>
                <button
                  onClick={() => setActiveTab("deletion-requests")}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
                    activeTab === "deletion-requests"
                      ? "border-red-500 text-red-600 dark:text-red-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400"
                  }`}
                >
                  Deletion Requests
                  {deletionRequests.length > 0 && (
                    <span className="inline-flex items-center justify-center px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">
                      {deletionRequests.length}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {activeTab === "users" && (
              <div className="flex flex-col items-end gap-1">
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    onClick={handleExportReferrals}
                    disabled={exportingReferrals}
                  >
                    {exportingReferrals ? t("exporting") : t("export_referrals")}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleExportOtpFallback}
                    disabled={exportingFallback}
                  >
                    {exportingFallback ? t("exporting") : t("export_otp_fallback")}
                  </Button>
                  {canCreate && (
                    <Button variant="primary" onClick={openCreate}>
                      + {t("createUser")}
                    </Button>
                  )}
                </div>
                {referralExportError && (
                  <span className="text-xs text-red-500">{referralExportError}</span>
                )}
                {fallbackExportError && (
                  <span className="text-xs text-red-500">{fallbackExportError}</span>
                )}
              </div>
            )}
          </div>
        </Card>

        {activeTab === "users" && (
          <DataTable
            className="flex-1 min-h-0"
            columns={columns}
            onFetch={handleFetch}
            filterDefs={filterDefs}
            initialState={{
              sortBy: "createdAt",
              sortOrder: "DESC",
              pageSize: 50,
            }}
            refreshKey={tableRefreshKey}
            actions={undefined}
            emptyMessage="No users found"
          />
        )}

        {activeTab === "deletion-requests" && (
          <Card className="flex-1 min-h-0 overflow-auto">
            {deletionRequestsLoading && (
              <div className="p-6 text-gray-500 dark:text-gray-400">Loading…</div>
            )}
            {deletionRequestsError && (
              <div className="p-6 text-red-500">{deletionRequestsError}</div>
            )}
            {!deletionRequestsLoading && !deletionRequestsError && deletionRequests.length === 0 && (
              <div className="p-6 text-gray-500 dark:text-gray-400">No pending deletion requests.</div>
            )}
            {!deletionRequestsLoading && deletionRequests.length > 0 && (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Requested At</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {deletionRequests.map((u) => (
                    <tr key={u.id}>
                      <td className="px-6 py-4">
                        <div className="font-medium text-gray-900 dark:text-gray-100">{u.fullName}</div>
                        {u.username && <div className="text-xs text-gray-500 dark:text-gray-400">@{u.username}</div>}
                        <div className="text-sm text-gray-500 dark:text-gray-400">{u.email}</div>
                        {u.phone && <div className="text-xs text-gray-400 dark:text-gray-500">{u.phone}</div>}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {u.deletionRequestedAt ? new Date(u.deletionRequestedAt).toLocaleString() : "—"}
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        {u.accountStatus === "anonymized" ? (
                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200">
                            Anonymized
                          </span>
                        ) : (
                          <>
                            {canUpdate && (
                              <Button size="sm" variant="secondary" onClick={() => handleCancelDeletion(u.id)}>
                                Cancel Request
                              </Button>
                            )}
                            {canDelete && (
                              <Button size="sm" variant="danger" onClick={() => handleConfirmDeletion(u.id)}>
                                Confirm Deletion
                              </Button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        )}

        {/* Create/Edit Dialog */}
        <Dialog
          open={dialogOpen}
          onClose={closeDialog}
          title={editing ? "Edit User" : "Create User"}
        >
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {submitError && (
              <div className="text-red-500 text-sm">{submitError}</div>
            )}

            <Input
              label="Full Name"
              {...register("fullName")}
              error={errors.fullName?.message}
            />

            <Input
              label="Username (optional)"
              {...register("username")}
              error={errors.username?.message}
            />

            <Input
              label="Email"
              type="email"
              {...register("email")}
              error={errors.email?.message}
            />

            <Input
              label="Phone"
              type="tel"
              {...register("phone")}
              error={errors.phone?.message}
            />

            <div>
              <Input
                label={t("location")}
                list="user-location-options"
                autoComplete="off"
                {...register("location")}
                error={errors.location?.message}
              />
              {/* ponytail: native <datalist> rather than a combobox component —
                  suggests without constraining, and needs no new dependency. */}
              <datalist id="user-location-options">
                {locations.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
            </div>

            <Input
              label="WhatsApp No"
              type="tel"
              {...register("whatsappNo")}
              error={errors.whatsappNo?.message}
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t("accountType")}
              </label>
              <select
                {...register("accountType")}
                disabled={!!editing}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 disabled:bg-gray-100 dark:disabled:bg-gray-800"
              >
                <option value="personal">{t("accountTypePersonal")}</option>
                <option value="broker">{t("accountTypeBroker")}</option>
                <option value="company">{t("accountTypeCompany")}</option>
              </select>
              {errors.accountType && (
                <p className="mt-1 text-xs text-red-500">{errors.accountType.message}</p>
              )}
            </div>

            {watch("accountType") === "company" && !editing && (
              <>
                <Input
                  label={t("companyName")}
                  {...register("companyName")}
                  error={errors.companyName?.message}
                />
                <Input
                  label={t("companyTradingName")}
                  {...register("companyTradingName")}
                  error={errors.companyTradingName?.message}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t("companyBio")}
                  </label>
                  <textarea
                    {...register("companyBio")}
                    rows={3}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </>
            )}

            {watch("accountType") === "broker" && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("linkedCompany")}
                </label>
                {editing?.companyId ? (
                  // ponytail: plain text rather than a disabled <select>. The companies
                  // fetch resolves after RHF applies companyId, so the select would have
                  // no matching <option> yet and render blank. Nothing is submitted from
                  // here either way — onSubmit suppresses companyId once it is set.
                  <>
                    <p className="text-sm text-gray-900 dark:text-gray-100">
                      {companies.find((c) => c.id === editing.companyId)?.name ?? editing.companyId}
                    </p>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      {t("companyAlreadyLinked")}
                    </p>
                  </>
                ) : (
                  <select
                    {...register("companyId")}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t("selectCompany")}</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            {!editing && (
              <div className="grid grid-cols-2 gap-3">
                {visibleDocumentFields.map(([field, labelKey]) => (
                  <div key={field}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t(labelKey)}
                    </label>
                    <input
                      type="file"
                      onChange={(e) =>
                        setFiles((prev) => ({ ...prev, [field]: e.target.files?.[0] ?? null }))
                      }
                      className="w-full text-sm text-gray-700 dark:text-gray-300"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Create-only: what "verified" means differs per account type, so the hint
                spells it out. Editing an existing user goes through the dedicated
                verify actions further down instead. */}
            {!editing && (
              <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    {...register("markVerified")}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                    {t("markVerified")}
                  </label>
                </div>
                <p className="mt-1 ml-6 text-xs text-gray-500 dark:text-gray-400">
                  {t(`markVerifiedHint_${selectedAccountType}`)}
                </p>
              </div>
            )}

            {/* No Role select: UserRole has only ADMIN, and a CMS-created marketplace
                user must keep the column default. Role is not editable here either. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Preferred Currency
              </label>
              <select
                {...register("preferredCurrency")}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                {USER_CURRENCIES.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-6">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  {...register("isActive")}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                  Active Account
                </label>
              </div>
            </div>

            {(editing?.accountType === "broker" || editing?.accountType === "company") && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t("charterBookingMode")}
                </label>
                <select
                  value={editing.charterBookingMode ?? "NON_INSTANT_ONLY"}
                  onChange={(e) =>
                    setEditing((prev) =>
                      prev ? { ...prev, charterBookingMode: e.target.value as User["charterBookingMode"] } : null
                    )
                  }
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="NON_INSTANT_ONLY">{t("charterBookingMode_NON_INSTANT_ONLY")}</option>
                  <option value="INSTANT_ONLY">{t("charterBookingMode_INSTANT_ONLY")}</option>
                  <option value="BOTH">{t("charterBookingMode_BOTH")}</option>
                </select>
              </div>
            )}

            {(editing?.accountType === "personal" || !!editing?.idFileUrl) && (
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t("id_verification")}
                </h3>
                {editing.idFileUrl ? (
                  <div className="flex flex-col gap-3">
                    <span className="text-sm font-mono text-gray-700 dark:text-gray-300 break-all">
                      {editing.idFileUrl.split('/').pop()?.split('?')[0] ?? 'id-document'}
                    </span>

                    {/* Download using file password */}
                    <div className="flex flex-col gap-1">
                      {!showFilePasswordInput ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => setShowFilePasswordInput(true)}
                        >
                          {t('download_id_document')}
                        </Button>
                      ) : (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              value={filePasswordInput}
                              onChange={(e) => setFilePasswordInput(e.target.value)}
                              placeholder={t('enter_file_password')}
                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-52"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={handleDownloadDoc}
                              disabled={downloadingDoc || !filePasswordInput}
                            >
                              {downloadingDoc ? t('loading') : t('download_id_document')}
                            </Button>
                          </div>
                          {fileDownloadError && (
                            <span className="text-xs text-red-600 dark:text-red-400">{fileDownloadError}</span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Recover file password using master key */}
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        className="text-xs text-blue-600 dark:text-blue-400 underline text-left w-fit"
                        onClick={() => setShowMasterKeyInput((v) => !v)}
                      >
                        {t('recover_file_password')}
                      </button>
                      {showMasterKeyInput && (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <input
                              type="password"
                              value={masterKeyInput}
                              onChange={(e) => setMasterKeyInput(e.target.value)}
                              placeholder={t('enter_master_key')}
                              className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 w-52"
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={handleRevealPassword}
                              disabled={revealingPassword || !masterKeyInput}
                            >
                              {revealingPassword ? t('loading') : t('reveal_doc_password')}
                            </Button>
                          </div>
                          {revealError && (
                            <span className="text-xs text-red-600 dark:text-red-400">{revealError}</span>
                          )}
                          {docPassword && (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-xs text-gray-500 dark:text-gray-400">{t('pdf_open_password')}:</span>
                              <span className="text-xs font-mono bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-700 text-yellow-800 dark:text-yellow-300 px-2 py-0.5 rounded select-all">
                                {docPassword}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Verify / unverify */}
                    <div>
                      {editing.isIdVerified ? (
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 dark:text-green-400">
                            ✓ {t("id_verified")}
                          </span>
                          {canUpdate && (
                            <Button
                              type="button"
                              size="sm"
                              variant="danger"
                              onClick={async () => {
                                try {
                                  await usersService.unverifyId(editing.id);
                                  setEditing((prev) => prev ? { ...prev, isIdVerified: false } : null);
                                  setTableRefreshKey((prev) => prev + 1);
                                } catch (err: any) {
                                  setSubmitError(getErrorMsg(err));
                                }
                              }}
                            >
                              {t("unverify_id")}
                            </Button>
                          )}
                        </div>
                      ) : (
                        canUpdate && (
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            onClick={async () => {
                              try {
                                await usersService.verifyId(editing.id);
                                setEditing((prev) => prev ? { ...prev, isIdVerified: true } : null);
                                setTableRefreshKey((prev) => prev + 1);
                              } catch (err: any) {
                                setSubmitError(getErrorMsg(err));
                              }
                            }}
                          >
                            {t("verify_id")}
                          </Button>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t("no_id_uploaded")}</p>
                )}
              </div>
            )}

            {editing?.accountType === "broker" && (editing.licenseFileUrl || editing.credentialsFileUrl) && (
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Broker Documents
                </h3>
                <div className="space-y-2">
                  {editing.licenseFileUrl && (
                    <BrokerDocRow
                      label="License File"
                      url={editing.licenseFileUrl}
                      onDownload={async () => {
                        const blob = await usersService.downloadLicenseDocument(editing.id);
                        triggerBlobDownload(blob, editing.licenseFileUrl!);
                      }}
                    />
                  )}
                  {editing.credentialsFileUrl && (
                    <BrokerDocRow
                      label="Credentials File"
                      url={editing.credentialsFileUrl}
                      onDownload={async () => {
                        const blob = await usersService.downloadCredentialsDocument(editing.id);
                        triggerBlobDownload(blob, editing.credentialsFileUrl!);
                      }}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Gap fill: same per-account-type document set the create form offers,
                minus whatever this account already uploaded. Documents already on
                file are shown by the download sections above and cannot be replaced
                here — their password has already been mailed out. */}
            {editing && missingDocumentFields.length > 0 && (
              <div className="mt-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  {t("uploadMissingDocuments")}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {t("uploadMissingDocumentsHint")}
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {missingDocumentFields.map(([field, labelKey]) => (
                    <div key={field}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t(labelKey)}
                      </label>
                      <input
                        type="file"
                        onChange={(e) =>
                          setFiles((prev) => ({ ...prev, [field]: e.target.files?.[0] ?? null }))
                        }
                        className="w-full text-sm text-gray-700 dark:text-gray-300"
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end space-x-2 mt-6">
              <Button type="button" variant="secondary" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                Save
              </Button>
            </div>
          </form>
        </Dialog>

        {/* Schedule Deletion Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          title={t("schedule_deletion_title")}
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-700 dark:text-gray-300">{t("schedule_deletion_body")}</p>

            {userToDelete?.accountType === "company" && (
              <div className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:bg-amber-900/20">
                <p className="text-sm font-medium">{t("schedule_deletion_owner_note")}</p>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="companyAction"
                    checked={companyAction === "transfer"}
                    disabled={transferCandidates.length === 0}
                    onChange={() => setCompanyAction("transfer")}
                  />
                  {t("transfer_ownership")}
                </label>
                {companyAction === "transfer" && (
                  transferCandidates.length > 0 ? (
                    <select
                      className="w-full rounded border-gray-300 text-sm dark:bg-gray-800"
                      value={newOwnerId}
                      onChange={(e) => setNewOwnerId(e.target.value)}
                    >
                      <option value="">{t("select_new_owner")}</option>
                      {transferCandidates.map((c) => (
                        <option key={c.id} value={c.id}>{c.fullName} ({c.email})</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-xs text-amber-700">{t("no_transfer_candidates")}</p>
                  )
                )}
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="radio"
                    name="companyAction"
                    checked={companyAction === "delete"}
                    onChange={() => setCompanyAction("delete")}
                  />
                  {t("delete_company")}
                </label>
                {companyAction === "delete" && (
                  <p className="text-xs text-amber-700">{t("delete_company_warning")}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-xs text-gray-500 mb-1">{t("deletion_reason_optional")}</label>
              <textarea
                className="w-full rounded border-gray-300 text-sm dark:bg-gray-800"
                rows={2}
                value={deletionReason}
                onChange={(e) => setDeletionReason(e.target.value)}
              />
            </div>

            {deleteError && <p className="text-sm text-red-500">{deleteError}</p>}

            <div className="flex justify-end space-x-2">
              <Button variant="secondary" onClick={() => setDeleteDialogOpen(false)}>
                {t("cancel")}
              </Button>
              <Button variant="danger" onClick={executeDelete}>
                {t("schedule_deletion")}
              </Button>
            </div>
          </div>
        </Dialog>
      </div>
      {ConfirmDialog}
    </Layout>
  );
};

export default UsersList;
